import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedCard } from "@/components/ui/animated-card";
import { AnimatedButton } from "@/components/ui/animated-button";
import { StaggerContainer, StaggerItem } from "@/components/ui/stagger-container";
import { AnimatedNumber } from "@/components/ui/animated-number";
import { PieChart3D } from "@/components/ui/pie-chart-3d";
import { BarChartAnimated } from "@/components/ui/bar-chart-animated";
import { ProgressCircle } from "@/components/ui/progress-circle";
import { PulsingLoader } from "@/components/ui/pulsing-loader";
import { Button } from "@/components/ui/button";
import { Users, FileCheck, MapPin, TrendingUp, Clock, AlertCircle, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";

// Import shared hook from ministers feature
import { useUserProfile } from "@/features/ministers/hooks/useUserProfile";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useRefreshDashboard } from "@/hooks/useRefreshDashboard";

export default function Dashboard() {
  const { userProfile } = useUserProfile();
  const { data: stats, isLoading, error } = useDashboardStats();
  const { mutate: refreshDashboard, isPending: isRefreshing } = useRefreshDashboard();
  const { toast } = useToast();

  const handleRefresh = () => {
    refreshDashboard(undefined, {
      onSuccess: (data) => {
        toast({
          title: "Datos actualizados",
          description: `Dashboard actualizado en ${Math.round(data.duration_ms)}ms`,
        });
      },
      onError: (error) => {
        toast({
          title: "Error al actualizar",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <PulsingLoader label="Cargando estadísticas..." />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <AlertCircle className="size-12 text-destructive" />
        <p className="text-lg text-muted-foreground">Error al cargar estadísticas del dashboard</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header with Refresh Button */}
      <motion.div 
        className="gradient-primary shadow-soft rounded-3xl p-6 text-white md:p-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-3xl font-bold md:text-4xl">
              Bienvenido, {userProfile?.full_name?.split(' ')[0] || 'Administrador'}
            </h1>
            <p className="text-base opacity-90 md:text-lg">
              Panel de Control - Las Asambleas de Dios de Bolivia
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            variant="secondary"
            size="sm"
            className="gap-2"
          >
            <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Actualizando...' : 'Actualizar Datos'}
          </Button>
        </div>
      </motion.div>

      {/* Big Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <StaggerContainer className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          <StaggerItem>
            <Link to="/ministers" className="block">
              <Card className="group cursor-pointer border-0 bg-gradient-to-br from-accent/20 to-accent/10 shadow-card transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:shadow-2xl">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="rounded-xl bg-accent/20 p-3 transition-all duration-300 group-hover:scale-110 group-hover:bg-accent/30">
                      <Users className="size-8 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground transition-colors group-hover:text-accent">Ministros</h3>
                      <p className="text-sm text-muted-foreground">Gestionar ministros</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </StaggerItem>
          
          <StaggerItem>
            <Link to="/credential-requests" className="block">
              <Card className="group cursor-pointer border-0 bg-gradient-to-br from-primary/20 to-primary/10 shadow-card transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:shadow-2xl">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="rounded-xl bg-primary/20 p-3 transition-all duration-300 group-hover:scale-110 group-hover:bg-primary/30">
                      <FileCheck className="size-8 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground transition-colors group-hover:text-primary">Credenciales</h3>
                      <p className="text-sm text-muted-foreground">Solicitudes y gestión</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </StaggerItem>
          
          <StaggerItem>
            <div className="block cursor-not-allowed opacity-60">
              <Card className="border-0 bg-gradient-to-br from-purple-500/20 to-purple-600/10 shadow-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="rounded-xl bg-purple-500/20 p-3">
                      <MapPin className="size-8 text-purple-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground">Iglesias</h3>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">Gestionar iglesias</p>
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                          Próximamente
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </StaggerItem>
        </StaggerContainer>
      </motion.div>

      {/* Quick Stats Grid */}
      <StaggerContainer className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
        {/* Total Ministers */}
        <StaggerItem>
          <AnimatedCard variant="gradient" className="h-full border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground">
                Ministros Activos
              </CardTitle>
              <Users className="size-5 text-primary" />
            </CardHeader>
            <CardContent>
              <AnimatedNumber 
                value={stats.totalMinisters}
                className="text-2xl font-bold text-foreground md:text-3xl"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                <span className="font-semibold text-accent">+{stats.monthlyGrowth}%</span> este año
              </p>
            </CardContent>
          </AnimatedCard>
        </StaggerItem>

        {/* Pending Approvals */}
        <StaggerItem>
          <AnimatedCard variant="gradient" className="h-full border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground">
                Pendientes de Aprobar
              </CardTitle>
              <FileCheck className="size-5 text-secondary" />
            </CardHeader>
            <CardContent>
              <AnimatedNumber 
                value={stats.pendingApprovals}
                className="text-2xl font-bold text-foreground md:text-3xl"
              />
              <Link to="/credential-requests?status=SUBMITTED">
                <p className="mt-2 text-xs text-primary hover:underline">
                  Ver solicitudes →
                </p>
              </Link>
            </CardContent>
          </AnimatedCard>
        </StaggerItem>

        {/* Active Requests */}
        <StaggerItem>
          <AnimatedCard variant="gradient" className="h-full border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground">
                Solicitudes Activas
              </CardTitle>
              <TrendingUp className="size-5 text-accent" />
            </CardHeader>
            <CardContent>
              <AnimatedNumber 
                value={stats.activeRequests}
                className="text-2xl font-bold text-foreground md:text-3xl"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                En proceso de credencialización
              </p>
            </CardContent>
          </AnimatedCard>
        </StaggerItem>

        {/* Districts */}
        <StaggerItem>
          <AnimatedCard variant="gradient" className="h-full border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground">
                Distritos Activos
              </CardTitle>
              <MapPin className="size-5 text-primary" />
            </CardHeader>
            <CardContent>
              <AnimatedNumber 
                value={stats.districtsActive}
                className="text-2xl font-bold text-foreground md:text-3xl"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Con ministros registrados
              </p>
            </CardContent>
          </AnimatedCard>
        </StaggerItem>
      </StaggerContainer>

      {/* Charts Section */}
      <StaggerContainer className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Requests by Status */}
        <StaggerItem>
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="text-xl">Solicitudes por Estado</CardTitle>
              <CardDescription>Distribución de solicitudes {new Date().getFullYear()}</CardDescription>
            </CardHeader>
            <CardContent>
              <PieChart3D 
                data={stats.requestsByStatus.map(item => ({
                  name: item.status,
                  value: item.count,
                  fill: item.fill
                }))}
                height={350}
              />
            </CardContent>
          </Card>
        </StaggerItem>

        {/* Ministers by District */}
        <StaggerItem>
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="text-xl">Ministros por Distrito</CardTitle>
              <CardDescription>Top 10 distritos con más ministros</CardDescription>
            </CardHeader>
            <CardContent>
              <BarChartAnimated 
                data={stats.ministersByDistrict.map(item => ({
                  name: item.district,
                  value: item.count,
                  fill: item.fill
                }))}
                height={350}
              />
            </CardContent>
          </Card>
        </StaggerItem>

        {/* Ministers by Degree */}
        <StaggerItem>
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="text-xl">Ministros por Grado</CardTitle>
              <CardDescription>Distribución por nivel ministerial</CardDescription>
            </CardHeader>
            <CardContent>
              <PieChart3D 
                data={stats.ministersByDegree.map(item => ({
                  name: item.degree,
                  value: item.count,
                  fill: item.fill
                }))}
                height={350}
              />
            </CardContent>
          </Card>
        </StaggerItem>

        {/* Próximos Eventos - Coming Soon */}
        <StaggerItem>
          <div className="relative">
            <Card className="border-0 shadow-card">
              <CardHeader>
                <CardTitle className="text-xl">Próximos Eventos</CardTitle>
                <CardDescription>Eventos y actividades programadas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="pointer-events-none select-none space-y-4 blur-sm">
                  <div className="flex items-start space-x-3 border-b border-border pb-3">
                    <div className="mt-0.5 text-xl">📅</div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="truncate text-sm font-semibold text-foreground">Convención Nacional</p>
                      <p className="truncate text-xs text-muted-foreground">La Paz · 15 Abr 2026</p>
                      <p className="text-xs text-muted-foreground">En 29 días</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 border-b border-border pb-3">
                    <div className="mt-0.5 text-xl">🎓</div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="truncate text-sm font-semibold text-foreground">Retiro de Pastores</p>
                      <p className="truncate text-xs text-muted-foreground">Santa Cruz · 22 Abr 2026</p>
                      <p className="text-xs text-muted-foreground">En 36 días</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="mt-0.5 text-xl">🙌</div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="truncate text-sm font-semibold text-foreground">Cumbre de Jóvenes</p>
                      <p className="truncate text-xs text-muted-foreground">Cochabamba · 10 May 2026</p>
                      <p className="text-xs text-muted-foreground">En 54 días</p>
                    </div>
                  </div>
                </div>
                {/* Coming Soon Overlay */}
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/80 backdrop-blur-[2px]">
                  <div className="space-y-2 px-4 text-center">
                    <Clock className="mx-auto size-12 text-accent opacity-80" />
                    <h4 className="text-xl font-bold text-foreground">Próximamente</h4>
                    <p className="max-w-xs text-sm text-muted-foreground">
                      El módulo de eventos estará disponible pronto
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </StaggerItem>
      </StaggerContainer>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left: Progress & Quick Actions */}
        <motion.div 
          className="space-y-6 lg:col-span-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {/* Progress Section */}
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="text-xl">Estadísticas del Sistema</CardTitle>
              <CardDescription>Rendimiento general {new Date().getFullYear()}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 justify-items-center gap-8 sm:grid-cols-2">
                <motion.div 
                  className="flex flex-col items-center"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <ProgressCircle 
                    value={stats.totalMinisters > 0 ? Math.min(Math.round((stats.credentializedMinisters / stats.totalMinisters) * 100), 100) : 0} 
                    size="lg"
                    color="accent"
                    label="Ministros Credencializados"
                    sublabel={`${stats.credentializedMinisters} de ${stats.totalMinisters} ministros`}
                  />
                </motion.div>
                <motion.div 
                  className="flex flex-col items-center"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <ProgressCircle 
                    value={stats.totalRequests > 0 ? Math.min(Math.round((stats.completedRequests / stats.totalRequests) * 100), 100) : 0} 
                    size="lg"
                    color="primary"
                    label="Solicitudes Completadas"
                    sublabel={`${stats.completedRequests} de ${stats.totalRequests} solicitudes`}
                  />
                </motion.div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="text-xl">Acciones Rápidas</CardTitle>
              <CardDescription>Tareas comunes del sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Link to="/ministers/new" className="block">
                  <AnimatedButton 
                    variant="gradient" 
                    size="pill" 
                    className="w-full"
                  >
                    <Users className="mr-2 size-5" />
                    Nuevo Ministro
                  </AnimatedButton>
                </Link>
                <Link to="/credential-requests/new" className="block">
                  <AnimatedButton 
                    variant="secondary" 
                    size="pill" 
                    className="w-full"
                  >
                    <FileCheck className="mr-2 size-5" />
                    Nueva Solicitud
                  </AnimatedButton>
                </Link>
                <Link to="/credential-requests?status=SUBMITTED" className="block">
                  <AnimatedButton 
                    variant="accent" 
                    size="pill" 
                    className="w-full"
                    pulse
                  >
                    <Clock className="mr-2 size-5" />
                    Aprobar Solicitudes
                  </AnimatedButton>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Right: Actividad Reciente - Coming Soon */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="relative"
        >
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="text-xl">Actividad Reciente</CardTitle>
              <CardDescription>Últimas acciones en el sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="pointer-events-none select-none space-y-4 blur-sm">
                <div className="flex items-start space-x-3 border-b border-border pb-3">
                  <div className="mt-0.5 text-xl">✅</div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="truncate text-sm font-semibold text-foreground">Solicitud aprobada</p>
                    <p className="truncate text-xs text-muted-foreground">SC-26-012 - Santa Cruz</p>
                    <p className="text-xs text-muted-foreground">Hace 2 horas</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 border-b border-border pb-3">
                  <div className="mt-0.5 text-xl">👤</div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="truncate text-sm font-semibold text-foreground">Nuevo ministro registrado</p>
                    <p className="truncate text-xs text-muted-foreground">Juan Pérez - La Paz</p>
                    <p className="text-xs text-muted-foreground">Hace 3 horas</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 border-b border-border pb-3">
                  <div className="mt-0.5 text-xl">🖨️</div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="truncate text-sm font-semibold text-foreground">Credenciales impresas</p>
                    <p className="truncate text-xs text-muted-foreground">LP-26-008 - 15 ministros</p>
                    <p className="text-xs text-muted-foreground">Hace 5 horas</p>
                  </div>
                </div>
              </div>

              {/* Coming Soon Overlay */}
              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/80 backdrop-blur-[2px]">
                <div className="space-y-2 px-4 text-center">
                  <Clock className="mx-auto size-12 text-primary opacity-80" />
                  <h4 className="text-xl font-bold text-foreground">Próximamente</h4>
                  <p className="max-w-xs text-sm text-muted-foreground">
                    El sistema de registro de actividad estará disponible pronto
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Info Cards - Mobile Optimized */}
      <StaggerContainer className="grid grid-cols-1 gap-4 sm:grid-cols-2" staggerDelay={0.15}>
        <StaggerItem>
          <Card variant="outlined" className="bg-muted/30">
            <CardHeader>
              <CardTitle className="text-base">Ministros sin Credencial</CardTitle>
            </CardHeader>
            <CardContent>
              <AnimatedNumber 
                value={stats.ministersWithoutCredentials}
                className="flex items-baseline gap-2 text-3xl font-bold text-foreground"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Pendientes de credencialización
              </p>
            </CardContent>
          </Card>
        </StaggerItem>

        <StaggerItem>
          <Card variant="outlined" className="bg-muted/30">
            <CardHeader>
              <CardTitle className="text-base">Crecimiento Anual</CardTitle>
            </CardHeader>
            <CardContent>
              <AnimatedNumber 
                value={stats.monthlyGrowth}
                decimals={1}
                prefix="+"
                suffix="%"
                className="text-3xl font-bold text-accent"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Nuevos registros este año
              </p>
            </CardContent>
          </Card>
        </StaggerItem>
      </StaggerContainer>
    </div>
  );
}
